/**
 * Mini indicator dock — spec §F5.9, first half.
 *
 * "Dock: vertical, right edge, top 60%; 6 round 38px buttons [b21,c21,d2,e1,isfm,soc] in
 * domain colours, white border; hover scale 1.14 + **label pill** (dark, left of icon,
 * arrow); active = gold outline. Click toggles popover."
 *
 * The six ids and their icons are the mockup's `DOCK_INDS`/`DOCK_ICONS`; the colours come
 * from `/meta/config.domains` (§D1) via each indicator's `domain`, so the dock recolours
 * with the domain palette and never carries a second copy of it.
 *
 * **The label pill is a real element, not the mockup's `::after`.** `content: attr(...)` on
 * a `:hover` pseudo-element is invisible to assistive technology and unreachable by
 * keyboard — the dock would be six unlabelled coloured circles for anyone not using a
 * mouse. It is rendered on hover *and focus* here, and each button additionally carries the
 * name as its accessible name, so the visual pill is decoration over an already-labelled
 * control (`aria-hidden`). The arrow, the geometry and the colours are still the mockup's
 * (NOTES #230).
 *
 * An id the §D3 catalogue does not contain is skipped rather than drawn grey: the dock is a
 * shortcut to six real indicators, and a button that opens an empty popover is worse than
 * no button.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';

import { fetchConfig, fetchIndicators, queryKeys } from '../../lib/api';
import { Icon } from '../../lib/icons';
import { useAppStore } from '../../lib/state';
import { DOCK_ICONS, DOCK_INDS, type DockInd } from './dock';

/** Fallback fill for an indicator whose domain is not in `/meta/config` yet. */
const FALLBACK_COLOR = '#2E7D32';

export function IndicatorDock() {
  const { t } = useTranslation();
  const dockInd = useAppStore((s) => s.dockInd);
  const toggleDockInd = useAppStore((s) => s.toggleDockInd);
  const [hovered, setHovered] = useState<string | null>(null);

  const { data: indicators } = useQuery({
    queryKey: queryKeys.indicators(),
    queryFn: fetchIndicators,
  });
  const { data: config } = useQuery({ queryKey: queryKeys.config(), queryFn: fetchConfig });

  const rows = DOCK_INDS.map((id) => indicators?.find((i) => i.id === id)).filter(
    (i): i is NonNullable<typeof i> => Boolean(i),
  );
  if (rows.length === 0) return null;

  return (
    <div id="indDock" role="group" aria-label={t('dock.title')}>
      {rows.map((ind) => {
        const color = config?.domains.find((d) => d.id === ind.domain)?.color ?? FALLBACK_COLOR;
        const active = dockInd === ind.id;
        // Four of the six dock indicators (`d2`, `e1`, `isfm`, `soc`) are demo-only today.
        // They keep their slot — the dock is a fixed piece of furniture and a shrinking one
        // would read as a bug — but go dead, with the same explanation the picker gives.
        const disabled = !ind.active;
        return (
          <button
            key={ind.id}
            type="button"
            className={`dock-btn${active ? ' active' : ''}${disabled ? ' inactive' : ''}`}
            data-dock={ind.id}
            data-label={ind.name}
            style={{ background: color }}
            disabled={disabled}
            aria-pressed={active}
            aria-label={ind.name}
            title={disabled ? t('indicators.inactiveTitle') : (ind.full_name ?? ind.name)}
            onClick={() => toggleDockInd(ind.id)}
            onMouseEnter={() => setHovered(ind.id)}
            onMouseLeave={() => setHovered((h) => (h === ind.id ? null : h))}
            onFocus={() => setHovered(ind.id)}
            onBlur={() => setHovered((h) => (h === ind.id ? null : h))}
          >
            <Icon name={DOCK_ICONS[ind.id as DockInd] ?? 'chart'} size={16} />
            {hovered === ind.id ? (
              <span className="dock-lab" data-dock-label={ind.id} aria-hidden="true">
                {ind.name}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

export default IndicatorDock;
